import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { useChatsStore } from "../../store/chats";
import { useSettingsStore } from "../../store/settings";
import { useChat } from "../../hooks/useChat";
import { useAutoSpeak } from "../../hooks/useAutoSpeak";
import { MessageBubble } from "./MessageBubble";
import { InputArea } from "./InputArea";
import { SessionTabs } from "./SessionTabs";
import { TerminalPanel } from "./TerminalPanel";
import { PlanPanel } from "../plan/PlanPanel";
import { PlanProgressBar } from "../plan/PlanProgressBar";
import { StepReport, FinalReport } from "../reports/Reports";
import { VpsPrecheckBanner } from "../vps/VpsPrecheckBanner";
import { NarrationLayer } from "./NarrationLayer";
import { ModelSelector } from "./ModelSelector";
import { DiffPanel } from "../diff/DiffPanel";
import { Welcome } from "./Welcome";
import { FilePreviewPanel } from "./FilePreviewPanel";
import { useFilePreview } from "../../lib/filePreview";
// StatusIndicator removed — ActionLog inside MessageBubble replaces it
import { cn } from "../../lib/cn";

export function ChatView() {
  const active = useChatsStore((s) => s.sessions.find((ss) => ss.id === s.activeId));
  const mode = useSettingsStore((s) => s.displayMode);
  const isSimple = mode === "chat";
  const filePreviewOpen = useFilePreview((s) => !!s.path);

  const messages = active?.messages || [];
  const plan = active?.plan || null;
  const stepReports = active?.stepReports || [];
  const finalReport = active?.finalReport || null;
  const narrations = active?.narrations || [];
  const vpsPrecheck = active?.vpsPrecheck || null;
  const error = active?.error || null;
  const status = active?.status || "idle";
  const startedAt = active?.startedAt || null;
  const finishedAt = active?.finishedAt || null;

  const { send, cancel, streaming } = useChat();
  const autoSpeak = useSettingsStore((s) => s.autoSpeak);
  useAutoSpeak(autoSpeak);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedModel, setSelectedModel] = useState("aicores/flash");

  /**
   * Retry на StepReport: переотправляет последний user-message в активной сессии.
   * Это пере-запускает весь pipeline с того же запроса (заново план, шаги, отчёты).
   * Точечный re-execute одного шага требует серверной поддержки и пока не реализован.
   */
  function handleRetryFromTask() {
    if (streaming) return;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "user" && m.text.trim()) {
        send(m.text);
        return;
      }
    }
  }

  // Auto-scroll. Считаем «контент-сигнатуру» по длине текстов / отчётов / нараций
  // и количеству actions. Любое изменение во время стрима (даже дописывание текста
  // в существующее сообщение) — триггерит скролл вниз.
  // Дополнительно «прилипаем» только если пользователь уже у нижнего края: если
  // он проскроллил вверх читать предыдущий ответ — не дёргаем его обратно.
  const userScrolledUpRef = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // если ушли больше чем на 80px от низа — считаем что юзер сам читает выше
      userScrolledUpRef.current = distFromBottom > 80;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Сигнатура изменения видимого контента активной сессии. Меняется на каждом
  // приращении текста ассистента, плана, нараций, action-карточек.
  const contentSignature =
    (messages.length ? messages[messages.length - 1].text?.length ?? 0 : 0) +
    "_" +
    messages.length +
    "_" +
    (messages[messages.length - 1]?.actions?.length ?? 0) +
    "_" +
    stepReports.length +
    "_" +
    (stepReports[stepReports.length - 1]?.summary?.length ?? 0) +
    "_" +
    narrations.length +
    "_" +
    (plan?.completed ?? 0) +
    "_" +
    (plan?.items?.length ?? 0) +
    "_" +
    (finalReport?.markdown?.length ?? 0) +
    "_" +
    status;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (userScrolledUpRef.current) return; // юзер сам читает — не дёргаем
    // requestAnimationFrame нужен чтобы DOM успел отрендерить новый контент,
    // иначе scrollHeight ещё старый и докрутка идёт «не до конца».
    const raf = requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [contentSignature, active?.id]);

  // При смене активной сессии (клик по табу) — мгновенно к низу без анимации,
  // и сбрасываем «прилипание»: новая сессия — новое поведение.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    userScrolledUpRef.current = false;
    el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
  }, [active?.id]);

  return (
    <div className="flex flex-col h-full">
      <SessionTabs />

      {/* Progress bar always visible when plan exists */}
      {plan && (
        <div className="px-4 py-2 border-b border-border/40">
          <PlanProgressBar plan={plan} />
        </div>
      )}

      <div className={cn(
        "flex-1 grid min-h-0",
        filePreviewOpen
          ? "grid-cols-1 lg:grid-cols-[1fr_440px] gap-0"
          : plan
            ? "grid-cols-1 md:grid-cols-[1fr_320px] xl:grid-cols-[1fr_380px] gap-4"
            : "grid-cols-1",
      )}>
        {/* Main chat column */}
        <div className="flex flex-col min-h-0">
          <div ref={scrollRef} className="chat-scroll-pad flex-1 overflow-y-auto px-4 pb-4 pt-3 space-y-4">
            {vpsPrecheck && !isSimple && <VpsPrecheckBanner state={vpsPrecheck} />}
            {messages.length === 0 && <Welcome />}

            {messages.map((m) => {
              return <MessageBubble key={m.id} message={m} />;
            })}

            {/* DiffPanel — proposed_edit карточки с Apply/Reject (только в developer mode;
                 в simple mode применение правок происходит автоматически через backend
                 confirm-flow и не показывается пользователю). */}
            {!isSimple && <DiffPanel />}

            {/* === SIMPLE MODE: многофазный StatusIndicator + DoneTimer ===
                 StatusIndicator сам определяет фазу по событиям сессии:
                  - "Анализирую запрос…" пока нет actions/text
                  - "Читаю файлы проекта…" при read_file/list_files/search_code
                  - "Формирую ответ…" когда пошёл текст ответа
                  - "Готово" при done (показывается 1.5s, потом скрывается)
                 Никакого плана, action cards, step reports, terminal, diff panel.
            */}
            {isSimple && active && (
              <>
                {stepReports.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {stepReports.map((r, i) => (
                      <StepReport key={`${r.index}-${i}`} report={r} />
                    ))}
                  </div>
                )}
                {status === "done" && startedAt && finishedAt && (
                  <DoneTimer startedAt={startedAt} finishedAt={finishedAt} />
                )}
              </>
            )}

            {/* === DEVELOPER MODE: narration + step reports + terminal + final report === */}
            {!isSimple && (
              <>
                {narrations.length > 0 && <NarrationLayer narrations={narrations} />}
                {stepReports.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {stepReports.map((r, i) => (
                      <StepReport key={`${r.index}-${i}`} report={r} onRetry={handleRetryFromTask} />
                    ))}
                  </div>
                )}
                <TerminalPanel />
                {finalReport && <FinalReport report={finalReport} simple={false} />}
              </>
            )}

            {/* Simple mode: финальный отчёт показываем только если в нём есть полезный
                структурированный итог (например, таблица). Для простых задач финальный
                отчёт не нужен — текст ответа уже в MessageBubble. */}
            {isSimple && finalReport && finalReport.markdown && finalReport.markdown.length > 100 && (
              <FinalReport report={finalReport} simple={true} />
            )}

            {/* Ошибки в simple mode скрываем (пользователь не должен видеть технические детали). */}
            {error && !isSimple && (
              <div className="card p-3 text-danger text-small border-danger/40">{error}</div>
            )}
          </div>

          {/* Input area */}
          <div className="chat-scroll-pad px-4 pt-2 pb-3 border-t border-border/40" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            {!isSimple && (
              <div className="flex justify-end px-1 mb-1">
                <ModelSelector value={selectedModel} onChange={(p, m) => setSelectedModel(`${p}/${m}`)} />
              </div>
            )}
            <InputArea onSend={send} onCancel={cancel} streaming={streaming} />
          </div>
        </div>

        {/* Right panel with plan — always visible when plan exists */}
        {/* Right panel: file preview takes priority over plan */}
        {filePreviewOpen ? (
          <div className="hidden lg:block overflow-hidden">
            <FilePreviewPanel />
          </div>
        ) : plan ? (
          <div className="hidden md:block overflow-y-auto py-2 pr-2 min-w-0">
            <PlanPanel plan={plan} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// === Simple mode components ===

function DoneTimer({ startedAt, finishedAt }: { startedAt: number; finishedAt: number }) {
  const sec = Math.round((finishedAt - startedAt) / 1000);
  const display = sec < 60 ? `${sec} сек` : `${Math.floor(sec / 60)} мин ${sec % 60} сек`;
  return (
    <div className="flex items-center gap-2 text-small text-fg-subtle px-1 py-1">
      <Clock className="size-3.5" />
      <span>Готово за {display}</span>
    </div>
  );
}

// === Welcome выведен в отдельный компонент Welcome.tsx ===
